import React, { useState, useEffect } from "react";
import { loginUser, resetPassword, registerUser, supabase } from "../supabase";
import {
  Mail,
  Lock,
  User,
  KeyRound,
  Eye,
  EyeOff,
  ArrowRight,
  X,
  Check,
  MailCheck,
  Loader2,
} from "lucide-react";

function Login({ onLogin }) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [registerUsername, setRegisterUsername] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved credentials on component mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('saficuts_credentials');
    if (savedCredentials) {
      try {
        const { identifier: savedIdentifier, rememberMe: savedRememberMe } = JSON.parse(savedCredentials);
        if (savedRememberMe) {
          setIdentifier(savedIdentifier);
          setRememberMe(true);
        }
      } catch (error) {
        console.error('Error loading saved credentials:', error);
        localStorage.removeItem('saficuts_credentials');
      }
    }
  }, []);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleRegisterPasswordVisibility = () =>
    setShowRegisterPassword(!showRegisterPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await loginUser(identifier, password);
      if (typeof result === "string") {
        setError(result);
      } else {
        const { user, role } = result;

        // Save credentials if "Remember Me" is checked
        if (rememberMe) {
          localStorage.setItem('saficuts_credentials', JSON.stringify({
            identifier: identifier,
            rememberMe: true
          }));
        } else {
          // Remove saved credentials if "Remember Me" is unchecked
          localStorage.removeItem('saficuts_credentials');
        }

        onLogin({ user, isAdmin: role === "admin" });
      }
    } catch (err) {
      setError("Login fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.");
      console.error("Login-Fehler:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    if (registerPassword !== confirmPassword) {
      setError("Die Passwörter stimmen nicht überein.");
      setIsLoading(false);
      return;
    }

    try {
      const { data: codeCheck, error: codeError } = await supabase.functions.invoke(
        "validate-registration-code",
        { body: { code: authCode } }
      );
      if (codeError || !codeCheck?.valid) {
        setError("Ungültiger Authentifizierungscode.");
        setIsLoading(false);
        return;
      }
    } catch (err) {
      setError("Code konnte nicht überprüft werden. Bitte versuchen Sie es erneut.");
      setIsLoading(false);
      return;
    }

    try {
      const result = await registerUser(
        registerEmail,
        registerPassword,
        registerUsername,
        "customer"
      );

      if (result) {
        setError(result);
      } else {
        const loginResult = await loginUser(registerEmail, registerPassword);
        if (typeof loginResult === "string") {
          setError(loginResult);
        } else {
          const { user, role } = loginResult;
          onLogin({ user, isAdmin: role === "admin" });
          setShowPopup(true);
        }
      }

      setRegisterUsername("");
      setRegisterEmail("");
      setRegisterPassword("");
      setConfirmPassword("");
      setAuthCode("");
      setShowRegister(false);
    } catch (err) {
      setError("Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.");
      console.error("Registrierungs-Fehler:", err);
    } finally {
      setIsLoading(false);
    }
  };


  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsLoading(true);

    try {
      const result = await resetPassword(resetEmail);
      if (result) {
        setError(result);
      } else {
        setShowResetPassword(false);
        setShowEmailSentModal(true);
        // Nach 3 Sekunden Modal schließen
        setTimeout(() => {
          setShowEmailSentModal(false);
        }, 3000);
      }
    } catch (err) {
      setError("Fehler beim Zurücksetzen des Passworts.");
      console.error("Passwort-Reset-Fehler:", err);
    } finally {
      setIsLoading(false);
    }
  };


  const resetPasswordFlow = () => {
    setShowResetPassword(false);
    setShowEmailSentModal(false);
    setResetEmail("");
    setError("");
    setMessage("");
  };

  // Shared premium styling — warm surfaces, champagne focus, butter CTA
  const inputClass =
    "peer block w-full pl-11 pr-4 py-3 bg-coffee-900/60 border border-coffee-800 rounded-xl text-cream placeholder-stone-600 focus:border-champagne-500/60 focus:ring-2 focus:ring-champagne-500/15 focus:bg-coffee-900 disabled:opacity-50 outline-none transition duration-200 text-[15px]";
  const inputWithToggle = inputClass.replace("pr-4", "pr-11");
  const iconClass =
    "absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-stone-500 peer-focus:text-champagne-400 transition-colors duration-200 pointer-events-none";
  const labelClass = "block text-sm font-medium text-stone-400 mb-2";
  const primaryBtn =
    "group w-full bg-cream hover:bg-white text-coffee-950 font-semibold py-3.5 px-4 rounded-xl transition duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-[0_14px_30px_-14px_rgba(203,165,102,0.45)]";

  return (
    <div className="min-h-screen flex bg-coffee-950 animate-fade-in">
      {/* ===== LEFT PANEL - Brand (Desktop only) ===== */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-coffee-950 items-center justify-center overflow-hidden">
        {/* Background Image */}
        <img
          src="/saficuts.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.16]"
        />
        {/* Warm dark overlay + subtle vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-coffee-950 via-coffee-950/75 to-coffee-950/90" />
        <div className="absolute inset-0 shadow-[inset_0_0_190px_70px_rgba(13,10,7,0.92)]" />

        {/* Brand Content */}
        <div className="relative z-10 text-center px-12">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-coffee-900 border border-champagne-500/25 rounded-2xl mb-8 shadow-xl shadow-black/50">
            <img src="/icon.svg" alt="SafiCuts" className="w-14 h-14" />
          </div>
          <h1 className="text-6xl font-extrabold text-cream tracking-tight">SafiCuts</h1>
          <p className="text-champagne-400/80 text-lg mt-2 tracking-wide">by Omar</p>

          <div className="mt-8 flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-gradient-to-r from-transparent to-champagne-500/40" />
            <span className="w-1.5 h-1.5 rounded-full bg-champagne-400" />
            <span className="h-px w-10 bg-gradient-to-l from-transparent to-champagne-500/40" />
          </div>

          <p className="mt-6 text-stone-500 text-xs tracking-[0.25em] uppercase">
            Terminbuchung leicht gemacht
          </p>
        </div>
      </div>

      {/* ===== RIGHT PANEL - Form (Always visible) ===== */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center bg-coffee-950 p-5 sm:p-8 min-h-screen">
        <div className="w-full max-w-sm">

          {/* Mobile Logo (hidden on desktop) */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-coffee-900 border border-champagne-500/25 rounded-2xl mb-4">
              <img src="/icon.svg" alt="SafiCuts" className="w-9 h-9" />
            </div>
            <h1 className="text-3xl font-extrabold text-cream tracking-tight">SafiCuts</h1>
            <p className="text-champagne-400/80 text-sm mt-1 tracking-wide">by Omar</p>
          </div>

          {/* Form Card */}
          <div className="relative bg-coffee-900/50 border border-coffee-800 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-slide-up">
            {/* Subtle champagne top accent */}
            <div className="h-px bg-gradient-to-r from-transparent via-champagne-500/50 to-transparent" />

            {/* Tab Navigation */}
            <div className="flex px-2 pt-2 gap-1">
              <button
                className={`relative flex-1 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  !showRegister ? 'text-cream bg-champagne-400/[0.06]' : 'text-stone-500 hover:text-stone-300'
                }`}
                onClick={() => setShowRegister(false)}
              >
                Anmelden
                {!showRegister && <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-champagne-400 rounded-full" />}
              </button>
              <button
                className={`relative flex-1 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  showRegister ? 'text-cream bg-champagne-400/[0.06]' : 'text-stone-500 hover:text-stone-300'
                }`}
                onClick={() => setShowRegister(true)}
              >
                Registrieren
                {showRegister && <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-champagne-400 rounded-full" />}
              </button>
            </div>
            <div className="h-px bg-coffee-800 mt-2" />

            {/* Form Content */}
            <div className="p-6 sm:p-7">
              {/* Error/Success Messages */}
              {error && (
                <div className="mb-5 px-4 py-3 bg-red-950/40 border border-red-500/20 rounded-xl flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-300 text-sm leading-snug">{error}</p>
                </div>
              )}
              {message && (
                <div className="mb-5 px-4 py-3 bg-champagne-500/10 border border-champagne-500/25 rounded-xl flex items-start gap-3">
                  <Check className="w-5 h-5 text-champagne-400 mt-0.5 flex-shrink-0" />
                  <p className="text-champagne-200 text-sm leading-snug">{message}</p>
                </div>
              )}

              {/* Login Form */}
              {!showRegister ? (
                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label htmlFor="email" className={labelClass}>E-Mail-Adresse</label>
                    <div className="relative">
                      <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="ihre@email.de"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        required
                        disabled={isLoading}
                        className={inputClass}
                      />
                      <Mail className={iconClass} />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className={labelClass}>Passwort</label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="Ihr Passwort"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        className={inputWithToggle}
                      />
                      <Lock className={iconClass} />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        disabled={isLoading}
                        aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-stone-500 hover:text-cream disabled:opacity-50 transition-colors duration-200"
                      >
                        {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                      </button>
                    </div>
                  </div>

                  {/* Remember Me + Forgot */}
                  <div className="flex items-center justify-between">
                    <input
                      id="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={isLoading}
                      className="sr-only"
                    />
                    <label
                      htmlFor="remember-me"
                      className={`flex items-center cursor-pointer select-none group ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className={`relative w-[18px] h-[18px] border rounded-md transition-colors duration-200 ${
                        rememberMe ? 'bg-champagne-400 border-champagne-400' : 'bg-coffee-900 border-coffee-700 group-hover:border-stone-500'
                      }`}>
                        {rememberMe && <Check className="absolute inset-0 w-3 h-3 m-[2px] text-coffee-950" strokeWidth={3} />}
                      </div>
                      <span className="ml-2.5 text-sm text-stone-400 group-hover:text-stone-300 transition-colors duration-200">
                        Login merken
                      </span>
                    </label>

                    <button
                      type="button"
                      onClick={() => setShowResetPassword(true)}
                      className="text-sm text-stone-500 hover:text-champagne-400 transition-colors duration-200"
                    >
                      Passwort vergessen?
                    </button>
                  </div>

                  <button type="submit" disabled={isLoading} className={primaryBtn}>
                    {isLoading ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5" />
                        <span>Wird angemeldet...</span>
                      </>
                    ) : (
                      <>
                        <span>Anmelden</span>
                        <ArrowRight className="h-[18px] w-[18px] transition-transform duration-200 group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                /* Registration Form */
                <form onSubmit={handleRegister} className="space-y-5">
                  <div>
                    <label htmlFor="reg-username" className={labelClass}>Benutzername</label>
                    <div className="relative">
                      <input
                        id="reg-username"
                        type="text"
                        placeholder="Ihr Benutzername"
                        value={registerUsername}
                        onChange={(e) => setRegisterUsername(e.target.value)}
                        required
                        disabled={isLoading}
                        className={inputClass}
                      />
                      <User className={iconClass} />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reg-email" className={labelClass}>E-Mail-Adresse</label>
                    <div className="relative">
                      <input
                        id="reg-email"
                        type="email"
                        autoComplete="email"
                        placeholder="ihre@email.de"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        required
                        disabled={isLoading}
                        className={inputClass}
                      />
                      <Mail className={iconClass} />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reg-password" className={labelClass}>Passwort</label>
                    <div className="relative">
                      <input
                        id="reg-password"
                        type={showRegisterPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Sicheres Passwort"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        className={inputWithToggle}
                      />
                      <Lock className={iconClass} />
                      <button
                        type="button"
                        onClick={toggleRegisterPasswordVisibility}
                        disabled={isLoading}
                        aria-label={showRegisterPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-stone-500 hover:text-cream disabled:opacity-50 transition-colors duration-200"
                      >
                        {showRegisterPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reg-confirm-password" className={labelClass}>Passwort bestätigen</label>
                    <div className="relative">
                      <input
                        id="reg-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Passwort wiederholen"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        className={inputWithToggle}
                      />
                      <Lock className={iconClass} />
                      <button
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        disabled={isLoading}
                        aria-label={showConfirmPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-stone-500 hover:text-cream disabled:opacity-50 transition-colors duration-200"
                      >
                        {showConfirmPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="auth-code" className={labelClass}>Authentifizierungscode</label>
                    <div className="relative">
                      <input
                        id="auth-code"
                        type="text"
                        placeholder="Ihr Registrierungscode"
                        value={authCode}
                        onChange={(e) => setAuthCode(e.target.value)}
                        required
                        disabled={isLoading}
                        className={inputClass}
                      />
                      <KeyRound className={iconClass} />
                    </div>
                  </div>

                  <button type="submit" disabled={isLoading} className={primaryBtn}>
                    {isLoading ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5" />
                        <span>Wird registriert...</span>
                      </>
                    ) : (
                      <>
                        <span>Konto erstellen</span>
                        <ArrowRight className="h-[18px] w-[18px] transition-transform duration-200 group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Footer */}
          <p className="mt-6 text-center text-[11px] text-stone-600 leading-relaxed px-2">
            <span className="text-stone-500">Impressum:</span> Privat erstellt von Dominik Djohar Feltes, Kernstrasse 2, 75334 Straubenhardt ·{" "}
            <a href="mailto:uclmv@web.de" className="text-stone-500 hover:text-champagne-400 transition-colors duration-200">uclmv@web.de</a>
          </p>
        </div>
      </div>

      {/* ===== Modals ===== */}

      {/* Success Modal */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-coffee-900 border border-coffee-800 rounded-2xl shadow-2xl max-w-sm w-full p-7 animate-scale-in">
            <div className="w-14 h-14 bg-champagne-400/10 border border-champagne-400/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7 text-champagne-400" strokeWidth={2.5} />
            </div>
            <h3 className="text-xl font-bold text-cream text-center mb-2">Registrierung erfolgreich!</h3>
            <p className="text-stone-400 text-sm text-center mb-6 leading-relaxed">Willkommen bei SafiCuts! Sie sind jetzt eingeloggt.</p>
            <button onClick={() => setShowPopup(false)} className={primaryBtn}>
              Weiter
            </button>
          </div>
        </div>
      )}

      {/* Email Sent Modal */}
      {showEmailSentModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-coffee-900 border border-coffee-800 rounded-2xl shadow-2xl max-w-sm w-full p-7 animate-scale-in text-center">
            <div className="w-14 h-14 bg-champagne-400/10 border border-champagne-400/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <MailCheck className="w-7 h-7 text-champagne-400" strokeWidth={1.75} />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">E-Mail gesendet!</h3>
            <p className="text-stone-400 text-sm mb-1">Wir haben dir eine E-Mail mit dem Reset-Code geschickt.</p>
            <p className="text-stone-600 text-xs mb-6">Prüfe auch deinen Spam-Ordner!</p>
            <div className="flex items-center justify-center gap-2 text-stone-500 text-sm">
              <Loader2 className="animate-spin h-4 w-4" />
              <span>Weiterleitung in 3 Sekunden...</span>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-coffee-900 border border-coffee-800 rounded-2xl shadow-2xl max-w-sm w-full p-7 animate-scale-in">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-cream">Passwort zurücksetzen</h3>
              <button onClick={resetPasswordFlow} aria-label="Schließen" className="text-stone-500 hover:text-cream transition-colors duration-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-stone-400 text-sm mb-6 leading-relaxed">Geben Sie Ihre E-Mail-Adresse ein, um einen Reset-Link zu erhalten.</p>
            <form onSubmit={handleResetPassword}>
              <div className="mb-6">
                <label htmlFor="reset-email" className={labelClass}>E-Mail-Adresse</label>
                <div className="relative">
                  <input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    placeholder="ihre@email.de"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className={inputClass}
                  />
                  <Mail className={iconClass} />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={resetPasswordFlow}
                  className="flex-1 bg-coffee-800 hover:bg-coffee-700 text-stone-300 font-medium py-3 px-4 rounded-xl transition-colors duration-200"
                >
                  Abbrechen
                </button>
                <button type="submit" disabled={isLoading} className={`flex-1 ${primaryBtn}`}>
                  {isLoading ? <Loader2 className="animate-spin h-5 w-5" /> : <span>Senden</span>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
